/* eslint-disable react/no-danger */

import React from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { Option } from '@/app/components/search/components/Form';
import { DESTINATION_TYPE, type SearchInfoDetailData } from '@/app/search/types.ts';
import SortPopover from '@/app/components/search/components/Filters/verticalFilters/Fields/getSortControl/SortPopover.tsx';
import { SortValues } from '@/app/common/memsearch/types.ts';
import { Icon } from '@/app/common/icons/Icon';
import type { Localization } from '../../../../../common/Localization';
import { RankingContainer, RankingLabel, TooltipContainer } from './styled';

export const getRecommendedLabel = (
  isMobile: boolean,
  isListView: boolean | undefined,
  localization: Localization,
  t: TranslationFunction
): React.JSX.Element | string => {
  const { rankingLink } = localization;
  const rankingLinkLabel = rankingLink
    ? t('vacation.oss.indexcollapsed.ranking').replace(
        /__(.*?)__/g,
        `<br/><a href=${rankingLink} target="_blank" rel="noreferrer">$1</a>`
      )
    : '';

  const rankingTooltip = (
    <TooltipContainer>
      <div
        dangerouslySetInnerHTML={{
          __html: rankingLinkLabel,
        }}
      />
    </TooltipContainer>
  );

  if (isMobile) return t('vacation.oss.components.searchresults.sort.recommended');
  if (isListView && rankingLink) return <SortPopover rankingTooltip={rankingTooltip} />;

  return (
    <div>
      <RankingLabel>{t('vacation.oss.components.searchresults.sort.recommended')}</RankingLabel>
      {rankingLink && (
        <RankingContainer
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName === 'A') {
              e.stopPropagation();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'A') {
              e.stopPropagation();
            }
          }}
          dangerouslySetInnerHTML={{
            __html: rankingLinkLabel,
          }}
        />
      )}
    </div>
  );
};

export const getSortOptions = ({
  searchMode,
  searchInfoDetailData,
  isListView,
  localization,
  isMobile,
  t,
}: {
  searchMode: SearchModes;
  searchInfoDetailData: SearchInfoDetailData;
  isListView?: boolean;
  imageBasePath: string;
  localization: Localization;
  isMobile: boolean;
  t: TranslationFunction;
}): Option[] => {
  const iconCheck = isListView
    ? {}
    : {
        customSelectedIcon: (
          // Black: the sort radios tint it with a filter tuned for black.
          <Icon category="Semantics" name="icon-check" size={16} color="#000000" alt="" />
        ),
      };

  const distanceCity =
    searchInfoDetailData.destination.destinationType === DESTINATION_TYPE.CITY
      ? [
          {
            value: SortValues.DistanceCity,
            label: t('vacation.oss.components.searchresults.sort.distance_city'),
            ...iconCheck,
          },
        ]
      : [];
  const distanceAirport =
    searchMode === SearchModes.DP
      ? [
          {
            value: SortValues.DistanceAirport,
            label: t('vacation.oss.components.searchresults.sort.distance_airport'),
            ...iconCheck,
          },
        ]
      : [];

  return [
    {
      value: SortValues.Recommended,
      label: getRecommendedLabel(isMobile, isListView, localization, t),
      ...iconCheck,
    },
    {
      value: SortValues.PriceAsc,
      label: t('vacation.oss.components.searchresults.sort.price_asc'),
      ...iconCheck,
    },
    {
      value: SortValues.PriceDesc,
      label: t('vacation.oss.components.searchresults.sort.price_desc'),
      ...iconCheck,
    },
    {
      value: SortValues.CategoryDesc,
      label: t('vacation.oss.components.searchresults.sort.category_desc'),
      ...iconCheck,
    },
    {
      value: SortValues.CategoryAsc,
      label: t('vacation.oss.components.searchresults.sort.category_asc'),
      ...iconCheck,
    },
    {
      value: SortValues.TopReviewed,
      label: t('vacation.oss.components.searchresults.sort.top_reviewed'),
      ...iconCheck,
    },
    ...distanceCity,
    ...distanceAirport,
  ];
};
